import { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Anchor,
  Badge,
  Button,
  FileButton,
  Group,
  Image,
  Paper,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { IconLink, IconPhoto, IconTrash } from '@tabler/icons-react';

import { BasicButton } from '@components/Button';
import { toErrorMessage } from '@util/toErrorMessage';

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'webp',
  'svg',
  'avif',
  'bmp',
]);

function youtubeEmbedUrl(url) {
  if (!url) return null;
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.replace(/^www\./, '');
    if (host === 'youtu.be') {
      const id = parsed.pathname.split('/').filter(Boolean)[0];
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      if (parsed.pathname.startsWith('/embed/')) {
        return `https://www.youtube.com/embed/${parsed.pathname.split('/')[2]}`;
      }
      const id = parsed.searchParams.get('v');
      return id ? `https://www.youtube.com/embed/${id}` : null;
    }
  } catch {
    return null;
  }
  return null;
}

function isImageUrl(url) {
  if (!url) return false;
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    const extension = pathname.split('.').pop();
    return IMAGE_EXTENSIONS.has(extension);
  } catch {
    return false;
  }
}

function assetPreviewSrc(asset) {
  if (asset?.variety_formatted === 'image' && asset.filename_url) {
    return asset.filename_url;
  }
  if (asset?.external_link_url && isImageUrl(asset.external_link_url)) {
    return asset.external_link_url;
  }
  return null;
}

function AssetPreview({ asset }) {
  const imageSrc = assetPreviewSrc(asset);
  if (imageSrc) {
    return (
      <Image
        src={imageSrc}
        alt={asset.original_filename || 'Exercise image'}
        w={96}
        h={96}
        radius="sm"
        fit="cover"
      />
    );
  }

  const embed = youtubeEmbedUrl(asset.external_link_url);
  if (embed) {
    return (
      <iframe
        src={embed}
        title={asset.original_filename || 'Exercise video'}
        width={168}
        height={96}
        style={{ border: 0, borderRadius: 4, flexShrink: 0 }}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    );
  }

  return (
    <Badge size="sm" variant="light" leftSection={<IconLink size={12} />}>
      {asset.variety_formatted || 'link'}
    </Badge>
  );
}

export default function ExerciseAssetsPanel({
  exerciseId,
  assets = [],
  readOnly = false,
  onChanged = () => {},
}) {
  const [uploading, setUploading] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [savingLink, setSavingLink] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  function onUploadImage(file) {
    if (!file || !exerciseId || readOnly) return;

    const formData = new FormData();
    formData.append('tempfile', file);
    formData.append('exercise_id', exerciseId);
    formData.append('variety', '1');

    setUploading(true);
    axios
      .post('/assets/', formData)
      .then(() => {
        toast.success('Image uploaded');
        onChanged();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setUploading(false));
  }

  function onAddLink() {
    if (!exerciseId || readOnly) return;
    const url = linkUrl.trim();
    if (!url) {
      toast.error('Enter a URL');
      return;
    }

    const formData = new FormData();
    formData.append('exercise_id', exerciseId);
    formData.append('variety', '3');
    formData.append('external_link_url', url);

    setSavingLink(true);
    axios
      .post('/assets/', formData)
      .then(() => {
        toast.success('Link added');
        setLinkUrl('');
        onChanged();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setSavingLink(false));
  }

  function onRemove(assetId) {
    if (!assetId || readOnly) return;
    setRemovingId(assetId);
    axios
      .post(`/assets/${assetId}/remove/`, {})
      .then(() => {
        toast.success('Removed');
        onChanged();
      })
      .catch((error) => toast.error(toErrorMessage(error)))
      .finally(() => setRemovingId(null));
  }

  return (
    <Paper p="md" withBorder radius="sm">
      <Stack gap="md">
        <div>
          <Text fw={700}>Media</Text>
          <Text size="sm" c="dimmed">
            Uploaded images, image links, and YouTube embeds preview here. Other
            demo pages open in a new tab.
          </Text>
        </div>

        {assets.length === 0 ? (
          <Text size="sm" c="dimmed">
            No media yet.
          </Text>
        ) : (
          <Stack gap="sm">
            {assets.map((asset) => (
              <Group
                key={asset.id}
                justify="space-between"
                align="flex-start"
                wrap="nowrap"
              >
                <Group align="flex-start" gap="sm" style={{ flex: 1, minWidth: 0 }}>
                  <AssetPreview asset={asset} />
                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                    <Badge size="xs" variant="outline" w="fit-content">
                      {asset.variety_formatted}
                    </Badge>
                    {asset.external_link_url ? (
                      <Anchor
                        href={asset.external_link_url}
                        target="_blank"
                        rel="noreferrer"
                        size="sm"
                        style={{ wordBreak: 'break-all' }}
                      >
                        {asset.external_link_url}
                      </Anchor>
                    ) : (
                      <Text size="sm" c="dimmed" truncate>
                        {asset.original_filename || asset.content_type || 'Uploaded file'}
                      </Text>
                    )}
                  </Stack>
                </Group>
                {!readOnly && (
                  <BasicButton
                    text="Remove"
                    onClick={() => onRemove(asset.id)}
                    loading={removingId === asset.id}
                    disabled={Boolean(removingId) || uploading || savingLink}
                    color="red"
                    variant="outline"
                    leftSection={<IconTrash size={14} />}
                  />
                )}
              </Group>
            ))}
          </Stack>
        )}

        {!readOnly && (
          <Stack gap="sm">
            <Group>
              <FileButton
                onChange={onUploadImage}
                accept="image/*"
                disabled={uploading || savingLink}
              >
                {(props) => (
                  <Button
                    {...props}
                    size="xs"
                    variant="light"
                    leftSection={<IconPhoto size={14} />}
                    loading={uploading}
                    disabled={uploading || savingLink}
                  >
                    Upload image
                  </Button>
                )}
              </FileButton>
            </Group>
            <Group align="flex-end" grow>
              <TextInput
                label="Demo link"
                description="Image URL, YouTube, or any https URL"
                placeholder="https://…"
                value={linkUrl}
                onChange={(event) => setLinkUrl(event.currentTarget.value)}
                disabled={uploading || savingLink}
              />
              <BasicButton
                text={savingLink ? 'Adding…' : 'Add link'}
                onClick={onAddLink}
                loading={savingLink}
                disabled={uploading || savingLink || !linkUrl.trim()}
                color="gray"
                variant="light"
                leftSection={<IconLink size={14} />}
              />
            </Group>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}
